import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sparkles, Star, TrendingUp, Heart, Shield, ArrowRight } from 'lucide-react';

const SpiritualCTA = ({ 
  variant = 'default', // 'default', 'success', 'relationships', 'protection'
  title,
  subtitle,
  showKundliButton = true,
  showShopButton = false,
  className = ''
}) => {
  const variants = {
    default: {
      bg: 'from-maroon-600 via-maroon-700 to-saffron-700',
      icon: Sparkles,
      iconColor: 'text-gold-300',
      title: title || 'Unlock Your True Potential with Vedic Astrology',
      subtitle: subtitle || 'Discover your life path, overcome challenges, and achieve success through the wisdom of ancient astrology. Get personalized insights for career, relationships, health, and spiritual growth.',
      benefits: [
        'Overcome life hurdles & obstacles',
        'Achieve career & financial success',
        'Improve relationships & harmony',
        'Find your true life purpose'
      ]
    },
    success: {
      bg: 'from-gold-600 via-saffron-600 to-orange-600',
      icon: TrendingUp,
      iconColor: 'text-white',
      title: title || 'Achieve Success & Prosperity',
      subtitle: subtitle || 'Align with your planetary energies to unlock career growth, financial abundance, and professional success. Know the best times for important decisions.',
      benefits: [
        'Career advancement opportunities',
        'Financial prosperity & wealth',
        'Business success & growth',
        'Optimal timing for decisions'
      ]
    },
    relationships: {
      bg: 'from-maroon-600 via-rose-700 to-saffron-700',
      icon: Heart,
      iconColor: 'text-gold-200',
      title: title || 'Harmonize Your Relationships',
      subtitle: subtitle || 'Understand compatibility, improve communication, and strengthen bonds with family, friends, and partners through astrological insights.',
      benefits: [
        'Marriage & relationship compatibility',
        'Family harmony & understanding',
        'Better communication & love',
        'Resolve conflicts & misunderstandings'
      ]
    },
    protection: {
      bg: 'from-maroon-700 via-maroon-800 to-saffron-800',
      icon: Shield,
      iconColor: 'text-gold-300',
      title: title || 'Protect & Transform Your Life',
      subtitle: subtitle || 'Remove negative energies, overcome obstacles, and create positive changes in your life with spiritual remedies and astrological guidance.',
      benefits: [
        'Protection from negative energies',
        'Remove obstacles & doshas',
        'Spiritual healing & transformation',
        'Positive life changes'
      ]
    }
  };

  const config = variants[variant] || variants.default;
  const Icon = config.icon;

  return (
    <div className={`relative overflow-hidden rounded-2xl ${className}`}>
      {/* Decorative Background Pattern */}
      <div className={`absolute inset-0 bg-gradient-to-br ${config.bg} opacity-95`}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 border-4 border-white/20 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 border-4 border-white/20 rounded-full translate-x-1/2 translate-y-1/2"></div>
          <div className="absolute top-1/2 left-1/2 w-48 h-48 border-4 border-white/20 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        {/* Mandala Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 left-1/4 text-9xl">🔱</div>
          <div className="absolute top-3/4 right-1/4 text-9xl">🕉️</div>
          <div className="absolute bottom-1/4 left-1/2 text-9xl">☯️</div>
        </div>
      </div>

      <div className="relative z-10 p-8 md:p-12 text-white">
        <div className="max-w-4xl mx-auto">
          {/* Icon & Title */}
          <div className="flex items-center justify-center mb-6">
            <div className={`${config.iconColor} bg-white/20 rounded-full p-4 backdrop-blur-sm`}>
              <Icon className="w-8 h-8" />
            </div>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 font-serif">
            {config.title}
          </h2>
          
          <p className="text-lg md:text-xl text-center text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            {config.subtitle}
          </p>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {config.benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <Star className="w-5 h-5 text-gold-300 flex-shrink-0" />
                <span className="text-white font-medium">{benefit}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {showKundliButton && (
              <Link to="/">
                <Button 
                  size="lg"
                  className="bg-white text-maroon-700 hover:bg-gold-100 font-bold px-8 py-6 text-lg rounded-lg shadow-xl hover:shadow-2xl transition-all flex items-center gap-2 group"
                >
                  <span>Get Your Kundli</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            )}
            {showShopButton && (
              <Link to="/shop">
                <Button 
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-2 border-white text-white hover:bg-white/20 font-bold px-8 py-6 text-lg rounded-lg shadow-xl hover:shadow-2xl transition-all flex items-center gap-2"
                >
                  <span>Explore Spiritual Products</span>
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            )}
          </div>

          {/* Trust Badge */}
          <div className="mt-8 text-center">
            <p className="text-white/80 text-sm flex items-center justify-center gap-2">
              <span>🔱</span>
              <span>Trusted by thousands • Instant Results • Accurate Vedic Calculations</span>
              <span>🔱</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualCTA;

